import { isNativePlatform } from '@/shared/lib/platform/platformDetection'

/** RP ID configurado no Supabase Dashboard (domínio puro, sem https://). */
export const PASSKEY_RP_ID = 'app.synap.med.br'

let nativeBridgeReady = false
let nativeBridgeInitPromise: Promise<void> | null = null

/** Resultado em cache da verificação UVPAA no browser (null = ainda não verificado). */
let webUvpaaAvailable: boolean | null = null

const hasWebAuthnCredentialsApi = (): boolean =>
  typeof window !== 'undefined' &&
  window.isSecureContext &&
  'PublicKeyCredential' in window &&
  Boolean(window.PublicKeyCredential) &&
  'credentials' in navigator &&
  typeof navigator.credentials?.create === 'function' &&
  typeof navigator.credentials?.get === 'function'

/**
 * WebAuthn exige que o hostname da página seja o RP ID ou subdomínio dele.
 * Previews (ex.: *.vercel.app) e localhost não funcionam com RP ID app.synap.med.br.
 */
export const isPasskeyRpIdCompatible = (): boolean => {
  if (typeof window === 'undefined') return false
  if (isNativePlatform()) return true

  const host = window.location.hostname
  return host === PASSKEY_RP_ID || host.endsWith(`.${PASSKEY_RP_ID}`)
}

const probeWebUvpaa = async (): Promise<boolean> => {
  if (!hasWebAuthnCredentialsApi() || !isPasskeyRpIdCompatible()) {
    webUvpaaAvailable = false
    return false
  }

  try {
    webUvpaaAvailable = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
  } catch {
    webUvpaaAvailable = false
  }

  return webUvpaaAvailable
}

/**
 * Indica se o ambiente atual pode usar chaves de acesso.
 * Web: exige domínio compatível com o RP ID + WebAuthn + UVPAA confirmado.
 * Nativo: exige bridge instalado via `ensureNativePasskeyBridge()` (Fase 2).
 * Retorna false enquanto a capacidade web ainda não foi sondada (`refreshPasskeyCapability`).
 */
export const isPasskeySupported = (): boolean => {
  if (typeof window === 'undefined') return false

  if (isNativePlatform()) {
    return nativeBridgeReady
  }

  if (!isPasskeyRpIdCompatible() || !hasWebAuthnCredentialsApi()) return false

  return webUvpaaAvailable === true
}

/**
 * Atualiza a detecção de capacidade (UVPAA na web; bridge no nativo).
 * Chamar antes de exibir UI de chave de acesso.
 */
export const refreshPasskeyCapability = async (): Promise<boolean> => {
  if (isNativePlatform()) {
    await ensureNativePasskeyBridge()
    return nativeBridgeReady
  }

  if (!isPasskeyRpIdCompatible()) {
    webUvpaaAvailable = false
    return false
  }

  return probeWebUvpaa()
}

/**
 * Instala o shim de WebAuthn no shell nativo antes do primeiro uso.
 * Idempotente; permanece no-op seguro até a integração do plugin (Fase 2).
 */
export const ensureNativePasskeyBridge = async (): Promise<void> => {
  if (!isNativePlatform()) return

  if (nativeBridgeReady) return

  if (nativeBridgeInitPromise) {
    await nativeBridgeInitPromise
    return
  }

  nativeBridgeInitPromise = (async () => {
    nativeBridgeReady = false
  })()

  try {
    await nativeBridgeInitPromise
  } finally {
    nativeBridgeInitPromise = null
  }
}

/** Expõe estado interno para testes. */
export const __passkeySupportTestUtils = {
  reset(): void {
    nativeBridgeReady = false
    nativeBridgeInitPromise = null
    webUvpaaAvailable = null
  },
  setNativeBridgeReady(value: boolean): void {
    nativeBridgeReady = value
  },
  setWebUvpaaAvailable(value: boolean | null): void {
    webUvpaaAvailable = value
  },
}
